import { TinyEmitter } from "@expert/event-bus";
import type { ExpertUniversityEvents } from "@expert/expert-university";
import { useExpertUniversityStore } from "@expert/expert-university";
import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import type { Partner } from "@expert/shared-types";
import { env } from "@/config";
import type { AgentActivity, ChatMessage } from "@/sdk";
import { AgentSdkBase } from "@/sdk";
import { userCache } from "@/shared-utils/auth/userCache";
import { createTrainingSession } from "./api/requests";
import { createSimulatedVoiceTaskForExpertUniversity, ExpertUniversitySimulatedVoiceTask } from "./SimulatedVoiceTask";
import { TrainingSessionChallengeStatusEnum } from "./types";

export default class ExpertUniversitySdk extends AgentSdkBase<ExpertUniversitySimulatedVoiceTask> {
    public readonly serviceProvider = "connect";
    readonly sdkType = "ExpertUniversitySdk";

    static readonly logger = getLogger({ module: "Expert University SDK", supportTeam: "training" });
    public override agentActivities: readonly AgentActivity[];
    public override hasActivitySelfManagement?: boolean | undefined;
    private expertUniversitySdkEventBus = new TinyEmitter<ExpertUniversityEvents>(
        "expert-university-event-fired",
        false,
    );
    private mediaRecorder: MediaRecorder | null = null;
    private audioStream: MediaStream | null = null;
    private appId: string;

    private partner: Set<Partner> = new Set();

    constructor() {
        super();
        this.agentActivities = ["Training", "Available", "Offline"] as AgentActivity[];
        useExpertUniversityStore.getState().setEventBus(this.expertUniversitySdkEventBus);
        //event listeners
        this.expertUniversitySdkEventBus.on("websocket-opened", (event) => {
            const { sessionId } = useExpertUniversityStore.getState();
            ExpertUniversitySdk.logger.info({ event: event.websocketUrl, sessionId }, "Websocket connection opened");
            trackFullStoryEvent("Expert_University_websocket_opened", { websocketUrl: event.websocketUrl, sessionId });

            if (event.websocketUrl) {
                void this.openWebsocketConnection(event.websocketUrl);
            }
        });
        this.expertUniversitySdkEventBus.on("websocket-closed", (event) => {
            const { sessionId } = useExpertUniversityStore.getState();

            ExpertUniversitySdk.logger.info({ sessionId, event }, "Websocket connection closed");
            trackFullStoryEvent("Expert_University_websocket_closed", { websocketUrl: event.websocketUrl });
            this.resetWebsocket();
        });
        this.expertUniversitySdkEventBus.on("audio-event-received", (event) => {
            this.convertStringToAudioBlob(event.audioBlob);
            this.processAudioEvent();
        });
        this.expertUniversitySdkEventBus.on("start-simulation-training", (event) => {
            ExpertUniversitySdk.logger.info({ event }, "Simulation training started");
            trackFullStoryEvent("Expert_University_simulation_training_started", {
                profileId: event.profileId,
                partner: event.partner,
            });

            if (!this.partner.has(event.partner)) {
                this.partner.clear();
                this.partner.add(event.partner);
            }
            if (event.profileId) {
                void this.startSimulationTraining(event.profileId, event.partner);
            }
        });
        this.appId = crypto.randomUUID();
    }

    public getSdkEventBus(): TinyEmitter<ExpertUniversityEvents> {
        return this.expertUniversitySdkEventBus;
    }
    public override canHandleTaskType(task: ExpertUniversitySimulatedVoiceTask): boolean {
        return task instanceof ExpertUniversitySimulatedVoiceTask && task.profileId !== undefined;
    }

    public override acceptTask(): Promise<void> {
        return Promise.resolve();
    }

    public override get offlineAgentActivity(): AgentActivity {
        return "Offline" as AgentActivity;
    }

    public override get availableAgentActivity(): AgentActivity {
        return "Available" as AgentActivity;
    }

    public override get defaultAgentActivity(): AgentActivity | undefined {
        return undefined;
    }

    public override getPartners(): ReadonlySet<Partner> {
        return this.partner;
    }

    public override isAgentActivitySelectable(): boolean {
        return true;
    }

    // TODO figure out how to get the agent activity
    public override getAgentActivity(): AgentActivity {
        return "Training" as AgentActivity;
    }

    public override async hangupCall(task: ExpertUniversitySimulatedVoiceTask): Promise<void> {
        ExpertUniversitySdk.logger.info({ sessionid: task.sessionId }, "Hangup call:");
        trackFullStoryEvent("Expert_University_Call_Ended", { sessionId: task.sessionId });

        useExpertUniversityStore.getState().getWebsocket?.()?.close();
        this.onHangupCall(task);
        await this.onTaskComplete(task, "TrainingCompleted");
        this.expertUniversitySdkEventBus.emit("trigger-evaluation-results", {
            sessionId: task.sessionId,
            profileId: task.profileId,
        });
    }

    public override muteCall(task: ExpertUniversitySimulatedVoiceTask, shouldMute: boolean): Promise<void> {
        task.isMuted = shouldMute;
        this.onMuteCall(task, shouldMute);
        this.audioStream?.getAudioTracks().forEach((track) => {
            track.enabled = !shouldMute;
        });
        return Promise.resolve();
    }

    public override setAgentActivity(): Promise<void> {
        return Promise.resolve();
    }

    //Private methods
    private startSimulationTraining = async (profileId: string, partner: Partner) => {
        useExpertUniversityStore.getState().getWebsocket?.()?.close();
        const task = createSimulatedVoiceTaskForExpertUniversity(partner, profileId);
        useExpertUniversityStore.getState().setProfileId(task.profileId);
        useExpertUniversityStore.getState().setSessionId(task.sessionId);
        await this.onTaskCreated(task);
        await this.createTrainingSession(task.sessionId, task.profileId);
    };

    private createTrainingSession = async (sessionId: string, profileId: string) => {
        await createTrainingSession({
            SessionID: sessionId,
            AppId: this.appId,
            ChallengeId: profileId,
            ChallengeStatus: TrainingSessionChallengeStatusEnum.NOT_COMPLETE,
            EmployeeId: userCache.employeeId,
            SessionStartTime: new Date().toISOString(),
            Environment: env.isProdMode ? "prod" : "dev",
        });
    };

    private resetWebsocket(): void {
        const { sessionId } = useExpertUniversityStore.getState();
        this.mediaRecorder?.stream.getTracks().forEach((track) => {
            track.stop();
        });
        this.mediaRecorder?.stop();
        this.mediaRecorder = null;
        useExpertUniversityStore.getState().resetEvents();
        ExpertUniversitySdk.logger.info({ sessionId }, "Media recorder stopped");
    }

    private openWebsocketConnection(websocketURL: string): void {
        if (websocketURL) {
            void this.initMediaRecorder();
        }
    }

    private async handleDataAvailable(event: BlobEvent): Promise<void> {
        const { sendMessage, setAudioHeader, audioHeader } = useExpertUniversityStore.getState();
        if (event.data.size > 0) {
            const buffer = await event.data.arrayBuffer();
            sendMessage?.(buffer);
            if (!audioHeader) {
                setAudioHeader(buffer);
            }
        }
    }

    private async initMediaRecorder() {
        const { sessionId } = useExpertUniversityStore.getState();

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.audioStream = stream;
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorder.ondataavailable = (event) => void this.handleDataAvailable(event);
            mediaRecorder.start(1024); // Buffer size
            this.mediaRecorder = mediaRecorder;

            ExpertUniversitySdk.logger.info({ sessionId }, "Microphone access granted");
            return true;
        } catch (error: any) {
            ExpertUniversitySdk.logger.info({ error: error.message, sessionId }, "Microphone access denied");
            return false;
        }
    }

    private convertStringToAudioBlob(data: string): void {
        const cleanBase64 = data
            .trim()
            .replace(/^data:.*?base64,/, "") // remove data URI prefix if present
            .replace(/[^A-Za-z0-9+/=]/g, "");
        const decodedBase64Data = atob(cleanBase64);
        const byteNumbers = Array.from(decodedBase64Data).map((char) => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);
        const audioBlob = new Blob([byteArray], { type: "audio/wav" });
        const url = URL.createObjectURL(audioBlob);
        const audio = new Audio(url);
        useExpertUniversityStore.getState().audioElements?.push(audio);
    }

    private processAudioEvent(): void {
        const { isBotPlaying, audioElements, setIsBotPlaying } = useExpertUniversityStore.getState();
        if (isBotPlaying || audioElements?.length === 0) {
            return;
        }
        const audio = audioElements?.shift();
        if (!audio) {
            return;
        }
        setIsBotPlaying(true);
        audio.onended = () => {
            URL.revokeObjectURL(audio.src);
            setIsBotPlaying(false);
            this.processAudioEvent();
        };

        audio.onerror = (error) => {
            const { sessionId } = useExpertUniversityStore.getState();
            ExpertUniversitySdk.logger.info({ sessionId, error }, "Error with audio element");
            URL.revokeObjectURL(audio.src);
            setIsBotPlaying(false);
            this.processAudioEvent();
        };

        audio.play().catch((error) => {
            const { sessionId } = useExpertUniversityStore.getState();
            ExpertUniversitySdk.logger.info({ sessionId: sessionId }, "Error playing audio element:", error);
            setIsBotPlaying(false);
            this.processAudioEvent();
        });
    }

    // Not implemented methods

    public override updateAgentAttributes(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: updateAgentAttributes");
        return Promise.resolve();
    }

    public override rejectTask(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: rejectTask");
        return Promise.resolve();
    }

    public override wrapupTask(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: wrapupTask");
        return Promise.resolve();
    }

    public override completeTask(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: completeTask");
        return Promise.resolve();
    }

    public override cancelTask(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: cancelTask");
        return Promise.resolve();
    }

    public override fetchContactsForTask(_task: ExpertUniversitySimulatedVoiceTask): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: fetchContactsForTask");
        return Promise.resolve();
    }

    public override areSurveysAutomatic(): boolean {
        return false;
    }

    public override sendToSurvey(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: sendToSurvey");
        return Promise.resolve();
    }

    public override holdCall(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: holdCall");
        return Promise.resolve();
    }

    public override resumeCall(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: resumeCall");
        return Promise.resolve();
    }

    public override sendDtmfDigits(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: sendDtmfDigits");
        return Promise.resolve();
    }

    public override redirectCallToTFN(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: redirectCallToTFN");
        return Promise.resolve();
    }

    public override redirectCallToSIP(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: redirectCallToSIP");
        return Promise.resolve();
    }

    public override addConferenceParticipant(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: addConferenceParticipant");
        return Promise.resolve();
    }

    public override updateParticipant(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: updateParticipant");
        return Promise.resolve();
    }

    public override removeParticipant(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: removeParticipant");
        return Promise.resolve();
    }

    public override removePendingParticipant(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: removePendingParticipant");
        return Promise.resolve();
    }

    public override leaveConference(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: leaveConference");
        return Promise.resolve();
    }

    public override endConference(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: endConference");
        return Promise.resolve();
    }

    public override resetConferenceState(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: resetConferenceState");
        return Promise.resolve();
    }

    public override getMessages(): Promise<ChatMessage[]> {
        ExpertUniversitySdk.logger.debug("Method not implemented: getMessages");
        return Promise.resolve([]);
    }

    public override sendMessage(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: sendMessage");
        return Promise.resolve();
    }

    public override advanceReadStatus(): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: advanceReadStatus");
        return Promise.resolve();
    }

    public override sendTypingIndicator(_task: ExpertUniversitySimulatedVoiceTask): Promise<void> {
        ExpertUniversitySdk.logger.debug("Method not implemented: sendTypingIndicator");
        return Promise.resolve();
    }

    protected override createCustomerCallbackForTask(): Promise<string> {
        ExpertUniversitySdk.logger.debug("Method not implemented: createCustomerCallbackForTask");
        return Promise.resolve("");
    }

    protected override createOutboundCall(): Promise<string> {
        ExpertUniversitySdk.logger.debug("Method not implemented: createOutboundCall");
        return Promise.resolve("");
    }
}
